import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { sanitizeHtml } from '../utils/securityUtils';
import { useLanguage } from '../context/LanguageContext';
import { COUNTRY_PHONE_CODES } from '../utils/countryPhoneCodes';
import 'flag-icons/css/flag-icons.min.css';

const Shop = () => {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /** From storefront API when catalog is empty (token vs sales channel). */
  const [catalogHint, setCatalogHint] = useState(null);

  // Order modal state
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Invoice/receipt modal (shown after order success)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [lastCreatedOrder, setLastCreatedOrder] = useState(null);
  const [lastCreatedOrderForm, setLastCreatedOrderForm] = useState(null);
  const [lastSavedMockup, setLastSavedMockup] = useState(null);
  const [invoiceEmailSent, setInvoiceEmailSent] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [waitingForPayment, setWaitingForPayment] = useState(false);
  const checkoutPopupRef = useRef(null);
  const paymentPollRef = useRef(null);

  // Mockup preview state
  const [showMockupModal, setShowMockupModal] = useState(false);
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const dropdownRef = useRef(null);

  // Product detail modal (full card with full description)
  const [showProductDetailModal, setShowProductDetailModal] = useState(false);
  const [selectedProductForDetail, setSelectedProductForDetail] = useState(null);
  // Image enlarge modal
  const [showImageEnlargeModal, setShowImageEnlargeModal] = useState(false);
  const [enlargedImageUrl, setEnlargedImageUrl] = useState(null);

  // Position & Size editor state
  const [positionX, setPositionX] = useState(50);
  const [positionY, setPositionY] = useState(50);
  const [mockupScale, setMockupScale] = useState(100);

  // Mockup customization state
  const [mockupSize, setMockupSize] = useState('');
  const [mockupColor, setMockupColor] = useState('#FFFFFF');
  const [mockupQuality, setMockupQuality] = useState('Standard');
  const [savingMockup, setSavingMockup] = useState(false);
  
  // Saved mockup data (after Proceed)
  const [savedMockup, setSavedMockup] = useState(null);
  const mockupPreviewRef = React.useRef(null);

  // Size options by product type
  const sizeOptionsByType = {
    tshirt: [
      { value: 'XS', label: 'XS' },
      { value: 'S', label: 'S' },
      { value: 'M', label: 'M' },
      { value: 'L', label: 'L' },
      { value: 'XL', label: 'XL' },
      { value: 'XXL', label: 'XXL' },
      { value: '3XL', label: '3XL' }
    ],
    mug: [
      { value: '8oz', label: '8 oz (Small)' },
      { value: '11oz', label: '11 oz (Standard)' },
      { value: '15oz', label: '15 oz (Large)' },
      { value: '20oz', label: '20 oz (Extra Large)' }
    ],
    poster: [
      { value: 'A4', label: 'Small - A4 (2480 × 3508 px)' },
      { value: 'A3', label: 'Medium - A3 (3508 × 4961 px)' },
      { value: 'Large', label: 'Large (7200 × 10800 px)' }
    ],
    cap: [
      { value: 'S/M', label: 'S/M (54 – 58 cm)' },
      { value: 'L/XL', label: 'L/XL (58 – 62 cm)' }
    ],
    totebag: [
      { value: 'Small', label: 'Small (3600 × 4200 px)' },
      { value: 'Medium', label: 'Medium (3600 × 4200 px)' },
      { value: 'Large', label: 'Large (4200 × 4800 px)' },
      { value: 'XL', label: 'Extra Large (4800 × 5400 px)' }
    ]
  };

  const getDefaultMockupSize = (typeId) => {
    const opts = sizeOptionsByType[typeId];
    return opts?.[0]?.value ?? 'M';
  };

  // Hardcoded blank product template images (from Cloudinary CDN)
  const productTemplates = {
    tshirt: { title: 'T-Shirt', image: 'https://res.cloudinary.com/dez2cotie/image/upload/v1770725211/t-shirt.png' },
    mug: { title: 'Mug 11oz', image: 'https://res.cloudinary.com/dez2cotie/image/upload/v1770725116/mug-front.png' },
    poster: { title: 'Poster', image: 'https://res.cloudinary.com/dez2cotie/image/upload/v1770725125/poster-front.png' },
    cap: { title: 'Cap', image: 'https://res.cloudinary.com/dez2cotie/image/upload/v1770725092/cap-front.png' },
    totebag: { title: 'Tote Bag', image: 'https://res.cloudinary.com/dez2cotie/image/upload/v1770725155/totebag.png' },
  };

  // Order form state
  const [orderForm, setOrderForm] = useState({
    lineItems: [{ title: '', price: '', quantity: 1, grams: 1000, variant_id: null, product_id: null }],
    currency: 'EUR',
    totalTax: 0,
    email: '',
    first_name: '',
    last_name: '',
    address1: '',
    phone: '',
    phoneCountryCode: '+93',
    city: '',
    province: '',
    country: '',
    zip: ''
  });

  // Phone country dropdown (Create Order modal)
  const [showPhoneCountryDropdown, setShowPhoneCountryDropdown] = useState(false);
  const [phoneCountrySearchQuery, setPhoneCountrySearchQuery] = useState('');
  const [phoneDropdownPosition, setPhoneDropdownPosition] = useState({ top: 0, left: 0 });
  const phoneCountryDropdownRef = useRef(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('all');
  const [availableStreamers, setAvailableStreamers] = useState([]);
  const [streamersLoading, setStreamersLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const showToastMessage = useCallback((message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  }, []);

  // Invoice modal (used for legacy flows; checkout flow uses Shopify confirmation)

  const startPaymentPolling = useCallback((email, orderFormData, savedMockupData, pollSince, pollToken) => {
    const maxAttempts = 200;
    let attempts = 0;
    let stopped = false;

    const handlePaid = async (order) => {
      stopped = true;
      if (paymentPollRef.current) { clearInterval(paymentPollRef.current); paymentPollRef.current = null; }
      if (checkoutPopupRef.current) { checkoutPopupRef.current.close(); checkoutPopupRef.current = null; }
      setCreatingOrder(false);
      setWaitingForPayment(false);
      setShowCreateOrderModal(false);
      setSendingInvoice(true);
      try {
        const invRes = await fetch('/.netlify/functions/send-order-invoice', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order,
            lineItems: order.line_items || orderFormData.lineItems,
            savedMockup: savedMockupData,
            customerEmail: email
          })
        });
        const invResult = await invRes.json();
        setSendingInvoice(false);
        if (invResult.success) {
          setLastCreatedOrder(order);
          setLastCreatedOrderForm(orderFormData);
          setLastSavedMockup(savedMockupData || null);
          setInvoiceEmailSent(true);
          setShowInvoiceModal(true);
          showToastMessage('Payment received! Invoice sent to your email.', 'success');
        } else {
          showToastMessage('Payment received! Invoice could not be sent.', 'error');
        }
      } catch (invErr) {
        setSendingInvoice(false);
        showToastMessage('Payment received! Invoice could not be sent.', 'error');
      }
    };

    if (!pollSince || !pollToken) {
      console.warn('Order polling skipped: missing poll token (set SESSION_SECRET or ORDER_POLL_SECRET on Netlify)');
      return;
    }

    const sinceParam = `&since=${encodeURIComponent(pollSince)}`;
    const tokenParam = `&token=${encodeURIComponent(pollToken)}`;

    const checkOnce = async () => {
      try {
        const res = await fetch(`/.netlify/functions/shopify-check-order-status?email=${encodeURIComponent(email)}${sinceParam}${tokenParam}`);
        const data = await res.json();
        if (data.success && data.paid && data.order) {
          await handlePaid(data.order);
          return true;
        }
      } catch (e) { console.warn('Poll error:', e); }
      return false;
    };

    const poll = async () => {
      if (stopped) return;
      if (attempts >= maxAttempts) {
        if (paymentPollRef.current) { clearInterval(paymentPollRef.current); paymentPollRef.current = null; }
        setCreatingOrder(false);
        setWaitingForPayment(false);
        if (checkoutPopupRef.current) { checkoutPopupRef.current.close(); checkoutPopupRef.current = null; }
        return;
      }
      if (checkoutPopupRef.current?.closed) {
        if (paymentPollRef.current) { clearInterval(paymentPollRef.current); paymentPollRef.current = null; }
        for (let retry = 0; retry < 5 && !stopped; retry++) {
          await new Promise(r => setTimeout(r, 2000));
          const found = await checkOnce();
          if (found) return;
        }
        if (!stopped) {
          setCreatingOrder(false);
          setWaitingForPayment(false);
          showToastMessage('Checkout window closed. If you completed payment, your order is being processed.', 'info');
        }
        return;
      }
      attempts++;
      await checkOnce();
    };

    poll();
    paymentPollRef.current = setInterval(poll, 3000);
  }, [showToastMessage]);

  const closeCreateOrderModal = useCallback(() => {
    if (paymentPollRef.current) {
      clearInterval(paymentPollRef.current);
      paymentPollRef.current = null;
    }
    setWaitingForPayment(false);
    setCreatingOrder(false);
    setShowCreateOrderModal(false);
    setShowPhoneCountryDropdown(false);
    setPhoneCountrySearchQuery('');
  }, []);

  const handleFormChange = (field, value) => {
    setOrderForm(prev => ({ ...prev, [field]: value }));
  };

  const handleLineItemChange = (index, field, value) => {
    setOrderForm(prev => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const removeLineItem = (index) => {
    setOrderForm(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  };

  // Storefront cart → checkoutUrl; payment methods are whatever the shop has enabled (not chosen in app code)
  const handleCreateOrderClick = async () => {
    const validLineItems = orderForm.lineItems.filter(item => item.title.trim() !== '');
    if (validLineItems.length === 0) { showToastMessage('Please add at least one product', 'error'); return; }
    if (!savedMockup) { showToastMessage('Please generate a mockup before creating the order', 'error'); return; }
    if (!orderForm.email?.trim()) { showToastMessage('Customer email is required', 'error'); return; }
    if (!orderForm.first_name?.trim()) { showToastMessage('First name is required', 'error'); return; }
    if (!orderForm.last_name?.trim()) { showToastMessage('Last name is required', 'error'); return; }
    if (!orderForm.address1?.trim()) { showToastMessage('Address is required', 'error'); return; }

    const formatPhone = (phone, countryCode) => {
      if (!phone || typeof phone !== 'string') return null;
      const trimmed = phone.trim();
      const digits = trimmed.replace(/\D/g, '');
      if (!digits || digits.length < 6) return null;
      const prefix = (countryCode || orderForm.phoneCountryCode || '+93').replace(/\D/g, '');
      if (trimmed.startsWith('+')) return '+' + digits;
      return '+' + prefix + digits;
    };
    const formattedPhone = formatPhone(orderForm.phone, orderForm.phoneCountryCode);
    if (!formattedPhone) {
      showToastMessage(t('shopPhoneRequired'), 'error');
      return;
    }

    setCreatingOrder(true);

    // Open popup IMMEDIATELY in the click handler (before any async work)
    // so the browser treats it as a user-initiated action and doesn't block it.
    const popup = window.open('about:blank', 'shopify-checkout', 'width=600,height=800,scrollbars=yes,resizable=yes');

    try {
      const fullName = `${orderForm.first_name || ""} ${orderForm.last_name || ""}`.trim();
      const vendor = selectedProduct?.vendor || '';
      const noteAttributes = [
        { name: "email", value: orderForm.email || "" },
        { name: "name", value: fullName }
      ].filter(attr => attr.value.trim() !== "");
      if (vendor) {
        noteAttributes.push({ name: "vendor", value: vendor });
        noteAttributes.push({ name: "streamer", value: vendor });
      }
      if (savedMockup.type) noteAttributes.push({ name: "Type", value: savedMockup.type });
      if (savedMockup.size) noteAttributes.push({ name: "Size", value: savedMockup.size });
      if (savedMockup.color) noteAttributes.push({ name: "Color", value: savedMockup.color });
      if (savedMockup.quality) noteAttributes.push({ name: "Quality", value: savedMockup.quality });
      if (savedMockup.imageUrl) noteAttributes.push({ name: "Sample Image", value: savedMockup.imageUrl });
      if (savedMockup.positionX !== undefined) noteAttributes.push({ name: "PositionX", value: String(savedMockup.positionX) });
      if (savedMockup.positionY !== undefined) noteAttributes.push({ name: "PositionY", value: String(savedMockup.positionY) });

      const typeIdFromMockup = productTypes.find((p) => p.label === savedMockup.type)?.id || 'tshirt';
      const checkoutVariant = findVariantForMockupSelection(
        selectedProduct?.variants,
        typeIdFromMockup,
        savedMockup.size || getDefaultMockupSize(typeIdFromMockup),
        savedMockup.color,
        savedMockup.quality || 'Standard'
      );

      const shopifyProductId = (p) => {
        if (!p) return null;
        const raw = p.id ?? p.shopifyId;
        return raw != null && String(raw).trim() !== '' ? String(raw).trim() : null;
      };

      const cartData = {
        lines: validLineItems.map((item, index) => {
          const productId =
            index === 0
              ? shopifyProductId(selectedProduct) || (item.product_id != null ? String(item.product_id).trim() : null)
              : item.product_id != null ? String(item.product_id).trim() : null;
          return {
            variant_id:
              index === 0 && checkoutVariant?.id != null ? String(checkoutVariant.id) : String(item.variant_id),
            ...(productId ? { product_id: productId } : {}),
            quantity: item.quantity || 1
          };
        }),
        email: orderForm.email || null,
        phone: formattedPhone || null,
        note_attributes: noteAttributes,
        shipping_address: {
          country: orderForm.country || null
        }
      };

      const response = await fetch('/.netlify/functions/create-cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cartData)
      });
      const result = await response.json();

      if (result.success && result.checkoutUrl) {
        showToastMessage('Opening checkout...', 'success');
        if (!popup || popup.closed) {
          window.location.href = result.checkoutUrl;
        } else {
          popup.location.href = result.checkoutUrl;
          checkoutPopupRef.current = popup;
          setWaitingForPayment(true);
          const { pollSince: serverSince, pollToken } = result;
          if (!pollToken || !serverSince) {
            showToastMessage('Payment confirmation may not detect completion automatically. Check your email for the invoice.', 'info');
          } else {
            startPaymentPolling(orderForm.email, { ...orderForm, lineItems: validLineItems }, savedMockup, serverSince, pollToken);
          }
        }
      } else {
        if (popup && !popup.closed) popup.close();
        const errMsg = result.error || 'Unknown error';
        showToastMessage(`${errMsg}. Try using a different browser.`, 'error');
        setCreatingOrder(false);
      }
    } catch (err) {
      if (popup && !popup.closed) popup.close();
      console.error('Error creating checkout:', err);
      const errMsg = err?.message || err?.cause?.message || 'Failed to create checkout';
      showToastMessage(`${errMsg}. Try using a different browser.`, 'error');
      setCreatingOrder(false);
    }
  };

  const TYPE_LABEL_MAP = { tshirt: '👕 T-Shirt', mug: '☕ Mug', poster: '🖼️ Poster', totebag: '👜 Tote Bag', cap: '🧢 Cap', casquette: '🧢 Cap' };

  const findVariantByType = (variants, typeId, preferQuality = 'Standard') => {
    if (!variants?.length) return null;
    const typeLabel = TYPE_LABEL_MAP[typeId];
    const matching = variants.filter(v => {
      const opts = v.selectedOptions || [];
      return !typeLabel || opts.some(o => o.name === 'Type' && o.value === typeLabel);
    });
    const withQuality = matching.find(v => v.selectedOptions?.some(o => o.name === 'Quality' && o.value === preferQuality));
    return withQuality || matching[0];
  };

  /** Match Storefront variant by mockup options so cart uses the correct line when titles collide across products */
  const findVariantForMockupSelection = (variants, typeId, size, colorHex, quality) => {
    if (!variants?.length) return null;
    const typeLabel = TYPE_LABEL_MAP[typeId];
    const sizeVal = (size || '').trim();
    const colorNorm = (colorHex || '').replace(/^#/, '').toLowerCase();
    const q = quality || 'Standard';
    const variantHasTypeOption = variants.some((v) =>
      (v.selectedOptions || []).some((o) => o.name === 'Type')
    );

    let best = null;
    let bestScore = -1;

    for (const v of variants) {
      const opts = v.selectedOptions || [];
      let score = 0;

      if (typeLabel && variantHasTypeOption) {
        if (!opts.some((o) => o.name === 'Type' && o.value === typeLabel)) continue;
        score += 100;
      }

      if (sizeVal) {
        const sizeOpt = opts.find((o) => o.name === 'Size');
        if (sizeOpt && String(sizeOpt.value) === sizeVal) score += 50;
      }

      if (q) {
        const qOpt = opts.find((o) => o.name === 'Quality');
        if (qOpt && qOpt.value === q) score += 50;
      }

      if (colorNorm) {
        const co = opts.find((o) => /color|colour|couleur/i.test(o.name));
        if (co) {
          const val = String(co.value).replace(/^#/, '').toLowerCase();
          if (val === colorNorm || val.includes(colorNorm) || colorNorm.includes(val)) score += 50;
        }
      }

      if (score > bestScore) {
        bestScore = score;
        best = v;
      }
    }

    if (best) return best;
    return findVariantByType(variants, typeId, q) || variants[0];
  };

  const handleOrderNow = (product) => {
    setSelectedProduct(product);
    const availTypes = getAvailableTypes(product?.products);
    const firstTypeId = availTypes[0]?.id || 'tshirt';
    const firstVariant =
      findVariantForMockupSelection(product?.variants, firstTypeId, '', '', 'Standard') ||
      findVariantByType(product?.variants, firstTypeId) ||
      product.variants?.[0];
    setOrderForm({
      lineItems: [{
        title: product.name,
        price: firstVariant?.price || product.price,
        quantity: 1,
        grams: 1000,
        variant_id: firstVariant?.id != null ? String(firstVariant.id) : (product.variant_id != null ? String(product.variant_id) : null),
        product_id: product.id != null ? String(product.id) : (product.shopifyId != null ? String(product.shopifyId) : null)
      }],
      currency: 'EUR', totalTax: 0, email: '', productType: '',
      size: '', color: '#000000', quality: '',
      first_name: '', last_name: '', address1: '', phone: '', phoneCountryCode: '+93',
      city: '', province: '', country: '', zip: ''
    });
    setCurrentMockupProductType(firstTypeId);
    setShowCreateOrderModal(true);
    setActiveDropdownId(null);
  };

  const updateLineItemPrice = (typeId, quality = 'Standard') => {
    if (!selectedProduct?.variants) return;
    const variant = findVariantByType(selectedProduct.variants, typeId, quality);
    if (variant) {
      setOrderForm(prev => ({
        ...prev,
        lineItems: prev.lineItems.map((item, i) =>
          i === 0
            ? {
                ...item,
                price: variant.price,
                variant_id: variant.id != null ? String(variant.id) : item.variant_id,
                product_id: item.product_id ?? (selectedProduct?.id != null ? String(selectedProduct.id) : selectedProduct?.shopifyId != null ? String(selectedProduct.shopifyId) : null)
              }
            : item
        )
      }));
    }
  };

  // Product types options for mockup generation
  const productTypes = [
    { id: 'tshirt', label: 'T-Shirt', icon: '👕' },
    { id: 'mug', label: 'Mug', icon: '☕' },
    { id: 'poster', label: 'Poster', icon: '🖼️' },
    { id: 'cap', label: 'Cap', icon: '🧢' },
    { id: 'totebag', label: 'Tote Bag', icon: '👜' },
  ];

  const getAvailableTypes = (products) => {
    if (!products || typeof products !== 'object' || Object.keys(products).length === 0) return productTypes;
    if (Object.values(products).every(v => v)) return productTypes;
    return productTypes.filter(pt => {
      const key = pt.id === 'cap' ? 'casquette' : pt.id;
      return products[key] === true;
    });
  };

  useEffect(() => {
    return () => {
      if (paymentPollRef.current) clearInterval(paymentPollRef.current);
    };
  }, []);

  // Close mockup dropdown when clicking outside
  useEffect(() => {
    function handleClickOutsideDropdown(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdownId(null);
      }
    }
    if (activeDropdownId) {
      document.addEventListener('mousedown', handleClickOutsideDropdown);
    }
    return () => document.removeEventListener('mousedown', handleClickOutsideDropdown);
  }, [activeDropdownId]);

  // Update dropdown position when opening, and close when clicking outside
  useEffect(() => {
    if (showPhoneCountryDropdown && phoneCountryDropdownRef.current) {
      const rect = phoneCountryDropdownRef.current.getBoundingClientRect();
      setPhoneDropdownPosition({ top: rect.bottom + 4, left: rect.left });
    }
  }, [showPhoneCountryDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (phoneCountryDropdownRef.current && !phoneCountryDropdownRef.current.contains(event.target) &&
          !event.target.closest('[data-phone-country-dropdown]')) {
        setShowPhoneCountryDropdown(false);
      }
    };
    if (showPhoneCountryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPhoneCountryDropdown]);

  // Track current product type for regeneration
  const [currentMockupProductType, setCurrentMockupProductType] = useState('tshirt');

  // Open mockup editor (preview only, no API call)
  const openMockupEditor = (productType) => {
    if (!selectedProduct) return;
    setActiveDropdownId(null);
    setCurrentMockupProductType(productType);
    setMockupQuality('Standard');
    setMockupSize('');
    updateLineItemPrice(productType, 'Standard');
    setPositionX(50);
    setPositionY(50);
    setMockupScale(100);
    setShowMockupModal(true);
  };

  // Reset position to auto
  const resetPositionToAuto = () => {
    setPositionX(50);
    setPositionY(50);
    setMockupScale(100);
  };

  // Center design
  const centerDesign = () => {
    setPositionX(50);
    setPositionY(50);
  };

  // Close mockup modal
  const closeMockupModal = () => {
    setShowMockupModal(false);
    setPositionX(50);
    setPositionY(50);
    setMockupScale(100);
    setMockupColor('#FFFFFF');
    setMockupQuality('Standard');
    setMockupSize('');
    setCurrentMockupProductType(null);
  };

  // Save mockup to Cloudinary and close modal
  const handleProceedMockup = async () => {
    if (!currentMockupProductType) {
      showToastMessage('Please select a product type', 'error');
      return;
    }
    if (!mockupSize) {
      showToastMessage('Please select a size', 'error');
      return;
    }

    const matchedVariant = findVariantForMockupSelection(
      selectedProduct?.variants,
      currentMockupProductType,
      mockupSize,
      mockupColor,
      currentMockupProductType === 'tshirt' ? mockupQuality : 'Standard'
    );
    if (!matchedVariant?.id) {
      showToastMessage('Could not match size/color/quality to a Shopify variant for this product.', 'error');
      return;
    }
    setOrderForm((prev) => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) =>
        i === 0
          ? {
              ...item,
              price: matchedVariant.price,
              variant_id: String(matchedVariant.id),
              title: prev.lineItems[0]?.title || selectedProduct?.name || '',
              product_id: item.product_id ?? (selectedProduct?.id != null ? String(selectedProduct.id) : selectedProduct?.shopifyId != null ? String(selectedProduct.shopifyId) : null)
            }
          : item
      )
    }));

    setSavingMockup(true);
    try {
      // Capture the mockup preview as an image using canvas
      const previewContainer = mockupPreviewRef.current;
      if (!previewContainer) {
        throw new Error('Preview container not found');
      }

      // Create main canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 800;
      canvas.height = 800;

      // Fill with neutral gray background
      ctx.fillStyle = '#374151';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Load the product template
      const templateImg = new Image();
      templateImg.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        templateImg.onload = resolve;
        templateImg.onerror = reject;
        templateImg.src = productTemplates[currentMockupProductType]?.image;
      });
      
      // Create a temporary canvas for colorizing the product template
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      
      // Draw the template on temp canvas
      tempCtx.drawImage(templateImg, 0, 0, tempCanvas.width, tempCanvas.height);

      // Apply color tint only if not white
      const colorHex = mockupColor.startsWith('#') ? mockupColor : '#FFFFFF';
      if (colorHex !== '#FFFFFF') {
        tempCtx.globalCompositeOperation = 'multiply';
        tempCtx.fillStyle = colorHex;
        tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
        tempCtx.globalCompositeOperation = 'destination-in';
        tempCtx.drawImage(templateImg, 0, 0, tempCanvas.width, tempCanvas.height);
        tempCtx.globalCompositeOperation = 'source-over';
      }

      // Draw the colorized template onto main canvas
      ctx.drawImage(tempCanvas, 0, 0);

      // Draw the design overlay
      if (selectedProduct?.image) {
        const designImg = new Image();
        designImg.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          designImg.onload = resolve;
          designImg.onerror = reject;
          designImg.src = selectedProduct.image;
        });

        const scale = mockupScale / 100;
        const designWidth = canvas.width * 0.5 * scale;
        const designHeight = canvas.height * 0.5 * scale;
        const designX = (canvas.width * positionX / 100) - (designWidth / 2);
        const designY = (canvas.height * positionY / 100) - (designHeight / 2);
        
        ctx.globalAlpha = 0.85;
        ctx.drawImage(designImg, designX, designY, designWidth, designHeight);
        ctx.globalAlpha = 1;
      }

      // Convert canvas to base64
      const imageData = canvas.toDataURL('image/png');

      // Delete previous mockup if exists
      if (savedMockup?.publicId) {
        try {
          await fetch('/.netlify/functions/cloudinary-delete-mockup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ publicId: savedMockup.publicId })
          });
        } catch (e) {
          console.warn('Failed to delete previous mockup:', e);
        }
      }

      // Upload to Cloudinary (merch-platform/order folder)
      const response = await fetch('/.netlify/functions/cloudinary-upload-mockup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image: imageData,
          folder: 'merch-platform/order'
        })
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to upload mockup');
      }

      // Save mockup data
      const productTypeName = productTypes.find(p => p.id === currentMockupProductType)?.label || currentMockupProductType;
      setSavedMockup({
        imageUrl: result.url,
        publicId: result.publicId,
        type: productTypeName,
        size: mockupSize,
        color: mockupColor,
        quality: currentMockupProductType === 'tshirt' ? mockupQuality : null,
        positionX: positionX,
        positionY: positionY,
        scale: mockupScale
      });

      showToastMessage('Mockup saved successfully!', 'success');
      setShowMockupModal(false);

    } catch (error) {
      console.error('Error saving mockup:', error);
      showToastMessage('Failed to save mockup: ' + error.message, 'error');
    } finally {
      setSavingMockup(false);
    }
  };

  // Edit mockup - reopen the mockup modal with existing settings
  const handleEditMockup = () => {
    if (savedMockup) {
      const productType = productTypes.find(p => p.label === savedMockup.type);
      if (productType) {
        setCurrentMockupProductType(productType.id);
      }
      setMockupSize(savedMockup.size || '');
      setMockupColor((savedMockup.color && savedMockup.color.startsWith('#')) ? savedMockup.color : '#FFFFFF');
      setMockupQuality(savedMockup.quality || 'Standard');
      setPositionX(savedMockup.positionX || 50);
      setPositionY(savedMockup.positionY || 50);
      setMockupScale(savedMockup.scale || 100);
      setShowMockupModal(true);
    }
  };

  // Fetch ALL products from Shopify (no vendor filter)
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch('/.netlify/functions/shopify-storefront-products?limit=50');
        const text = await response.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          throw new Error(`Failed to fetch products: ${response.status}`);
        }
        if (!response.ok) {
          throw new Error(data?.message || `Failed to fetch products: ${response.status}`);
        }

        if (data.success && Array.isArray(data.products)) {
          setProducts(data.products);
          setCatalogHint(data.catalogHint || null);
        } else {
          throw new Error(data.message || 'Failed to fetch products');
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Fetch available streamers for filter dropdown
  useEffect(() => {
    const fetchStreamers = async () => {
      try {
        setStreamersLoading(true);
        const response = await fetch('/.netlify/functions/get-websites');
        const result = await response.json();
        if (response.ok && result.success && result.websites) {
          setAvailableStreamers(result.websites);
        } else {
          setAvailableStreamers([]);
        }
      } catch (error) {
        console.error('Error fetching streamers:', error);
        setAvailableStreamers([]);
      } finally {
        setStreamersLoading(false);
      }
    };
    fetchStreamers();
  }, []);

  // Get unique vendors for filter (fallback)
  const vendors = [...new Set(products.map(p => p.vendor).filter(Boolean))].sort();

  // Helper to check if description is long (for "See more")
  const isDescriptionLong = (html) => (html || '').replace(/<[^>]*>/g, '').trim().length > 80;

  // Helper to format date for display
  const formatProductDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return null;
    }
  };

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = !searchTerm || 
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.vendor?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesVendor = selectedVendor === 'all' || product.vendor === selectedVendor;

    let matchesDate = true;
    if (product.createdAt) {
      const productDate = new Date(product.createdAt);
      if (dateFrom) {
        const from = new Date(dateFrom);
        from.setHours(0, 0, 0, 0);
        if (productDate < from) matchesDate = false;
      }
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        if (productDate > to) matchesDate = false;
      }
    }

    return matchesSearch && matchesVendor && matchesDate;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900">
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] pt-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-white mb-2">Loading Shop</h2>
            <p className="text-gray-400">Fetching products...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900">
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] pt-20">
          <div className="text-center">
            <i className="fas fa-exclamation-triangle text-red-400 text-5xl mb-4"></i>
            <h2 className="text-2xl font-bold text-white mb-2">Error Loading Shop</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <button onClick={() => window.location.reload()} className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-2 rounded-lg transition-colors">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Toast - above all modals (Create Order, Payment, Order Receipt) */}
      {showToast && (
        <div className="fixed top-4 right-4 z-[99999]">
          <div className={`px-6 py-3 rounded-lg shadow-lg ${toastType === 'success' ? 'bg-green-600' : 'bg-red-600'} text-white`}>
            {toastMessage}
          </div>
        </div>
      )}

      <div className="pt-20 pb-12">
        {/* Header */}
        <div className="text-center mb-8 px-4">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            <i className="fas fa-shopping-bag text-cyan-400 mr-3"></i>
            Shop
          </h1>
          <p className="text-xl text-gray-400 mb-2">
            {t('shopDiscoverProducts')}
          </p>
          {products.length > 0 && (
            <p className="text-gray-500">
              {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} available
            </p>
          )}
        </div>

        {/* Filter/Sort Controls */}
        {products.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 mb-8">
            <div className="bg-[#18181B] rounded-xl p-6 border border-gray-700 shadow-lg">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
                {/* Filter Title */}
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-600 to-pink-600 rounded-lg flex items-center justify-center">
                    <i className="fa-solid fa-filter text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg">Filters</h3>
                    <p className="text-gray-400 text-sm">{t('findPerfectProduct')}</p>
                  </div>
                </div>

                {/* Filter Controls */}
                <div className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
                  {/* Search */}
                  <div className="relative">
                    <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="bg-[#0F0F0F] border border-gray-600 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent hover:bg-gray-800 transition-colors min-w-[180px] h-10"
                      placeholder={t('searchProducts')}
                    />
                  </div>

                  {/* Divider */}
                  <div className="w-px h-8 bg-gray-600 hidden lg:block"></div>

                  {/* Streamer Filter */}
                  <div className="flex flex-col sm:flex-row items-center gap-2">
                    <label className="text-gray-300 font-medium text-sm whitespace-nowrap">Streamer :</label>
                    <div className="relative">
                      <select
                        value={selectedVendor}
                        onChange={(e) => setSelectedVendor(e.target.value)}
                        disabled={streamersLoading}
                        className="appearance-none bg-[#0F0F0F] border border-gray-600 rounded-lg px-4 py-2.5 pr-10 text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px] h-10"
                      >
                        <option value="all">All Streamers</option>
                        {availableStreamers.length > 0 ? (
                          availableStreamers.map((website) => {
                            const streamerName = website.replace('.com', '');
                            return (
                              <option key={streamerName} value={streamerName}>{streamerName}</option>
                            );
                          })
                        ) : (
                          vendors.map(vendor => (
                            <option key={vendor} value={vendor}>{vendor}</option>
                          ))
                        )}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                        {streamersLoading ? (
                          <i className="fa-solid fa-spinner fa-spin text-gray-400 text-xs"></i>
                        ) : (
                          <i className="fa-solid fa-chevron-down text-gray-400 text-xs"></i>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="w-px h-8 bg-gray-600 hidden lg:block"></div>

                  {/* Date Range Filter */}
                  <div className="flex flex-col sm:flex-row items-center gap-2">
                    <label className="text-gray-300 font-medium text-sm whitespace-nowrap">Date :</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="bg-[#0F0F0F] border border-gray-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent hover:bg-gray-800 transition-colors cursor-pointer h-10 [color-scheme:dark]"
                        placeholder="From"
                      />
                      <span className="text-gray-500 text-sm font-medium">to</span>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="bg-[#0F0F0F] border border-gray-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent hover:bg-gray-800 transition-colors cursor-pointer h-10 [color-scheme:dark]"
                        placeholder="To"
                      />
                      {(dateFrom || dateTo) && (
                        <button
                          onClick={() => { setDateFrom(''); setDateTo(''); }}
                          className="text-gray-400 hover:text-red-400 p-1.5 hover:bg-gray-700 rounded-lg transition-colors"
                          title="Clear dates"
                        >
                          <i className="fa-solid fa-times text-xs"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Filter Summary */}
              {(selectedVendor !== 'all' || dateFrom || dateTo || searchTerm) && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <i className="fa-solid fa-info-circle text-blue-400"></i>
                      <span className="text-gray-300">Active filters :</span>
                      {searchTerm && (
                        <span className="bg-cyan-900/40 text-cyan-400 px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5">
                          <i className="fa-solid fa-search text-[10px]"></i>
                          "{searchTerm}"
                        </span>
                      )}
                      {selectedVendor !== 'all' && (
                        <span className="bg-cyan-900/40 text-cyan-400 px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5">
                          <i className="fa-solid fa-user text-[10px]"></i>
                          {selectedVendor}
                        </span>
                      )}
                      {(dateFrom || dateTo) && (
                        <span className="bg-cyan-900/40 text-cyan-400 px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5">
                          <i className="fa-regular fa-calendar text-[10px]"></i>
                          {dateFrom && dateTo ? `${dateFrom} → ${dateTo}` : dateFrom ? `From ${dateFrom}` : `Until ${dateTo}`}
                        </span>
                      )}
                    </div>
                    <div className="text-gray-400 text-sm">
                      {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              
              <h3 className="text-2xl font-bold text-white mb-2">
                {products.length === 0 ? 'No Products Yet' : 'No Matching Products'}
              </h3>
              <p className="text-gray-400 max-w-xl mx-auto">
                {products.length === 0
                  ? catalogHint ||
                    'The shop catalog is loaded from Shopify’s Storefront API. If products appear in Admin but not here, check Netlify SHOPIFY_STOREFRONT_ACCESS_TOKEN and that each product is published to your Headless (custom storefront) sales channel.'
                  : 'Try adjusting your search or filter.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product) => (
                <div
                  key={`${String(product.id)}-${product.createdAt || product.updatedAt || ''}`}
                  className="bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-700 hover:border-gray-600 group flex flex-col h-full"
                >
                  {/* Product Image - clickable to enlarge */}
                  <div 
                    className="aspect-square bg-gray-700 relative overflow-hidden cursor-zoom-in shrink-0"
                    onClick={(e) => { e.stopPropagation(); setEnlargedImageUrl(product.image); setShowImageEnlargeModal(true); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEnlargedImageUrl(product.image); setShowImageEnlargeModal(true); } }}
                    aria-label="Enlarge image"
                  >
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/300x300?text=No+Image'; }}
                    />
                    {product.status !== 'active' && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <span className="text-white font-bold text-lg">Out of Stock</span>
                      </div>
                    )}
                    {/* Streamer Badge - Top Left */}
                    {product.vendor && (
                      <Link
                        to={`/${product.vendor}/shop`}
                        className="absolute top-3 left-3 z-10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="bg-black/70 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5 hover:bg-black/90 transition-colors">
                          <i className="fas fa-user text-cyan-400"></i>
                          <span>{product.vendor}</span>
                        </div>
                      </Link>
                    )}
                  </div>

                  <div className="flex flex-col flex-1 min-h-0">
                  {/* Product Info - clickable to open full card modal */}
                  <div 
                    className="p-4 cursor-pointer flex-1 flex flex-col"
                    onClick={() => { setSelectedProductForDetail(product); setShowProductDetailModal(true); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedProductForDetail(product); setShowProductDetailModal(true); } }}
                  >
                    <h3 className="text-lg font-bold text-white mb-1 overflow-hidden hover:text-cyan-400 transition-colors" style={{ 
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' 
                    }}>
                      {product.name}
                    </h3>
                    
                    {product.description && (
                      <div className="mb-3">
                        <div 
                          className="text-gray-400 text-sm overflow-hidden"
                          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.description) }}
                        />
                        {isDescriptionLong(product.description) && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setSelectedProductForDetail(product); setShowProductDetailModal(true); }}
                            className="text-cyan-400 hover:text-cyan-300 text-sm font-medium mt-1 inline-flex items-center gap-1"
                          >
                            {t('seeMore')}
                            <i className="fas fa-chevron-right text-xs"></i>
                          </button>
                        )}
                      </div>
                    )}

                    {product.stock > 0 && (
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-xs text-gray-500">
                          {product.stock} in stock
                        </div>
                      </div>
                    )}

                    {product.createdAt && (
                      <div className="mb-3">
                        <span className="inline-flex items-center gap-1.5 bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded">
                          <i className="fa-regular fa-calendar text-gray-400"></i>
                          {formatProductDate(product.createdAt)}
                        </span>
                      </div>
                    )}
                  </div>

                    {/* Order Now — pinned to card bottom */}
                    <div className="px-4 pb-4 pt-0 mt-auto shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button 
                      onClick={() => handleOrderNow(product)}
                      className={`w-full py-2.5 px-4 rounded-lg font-medium transition-all duration-200 ${
                        product.status === 'active' 
                          ? 'bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white hover:shadow-lg' 
                          : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      }`}
                      disabled={product.status !== 'active'}
                    >
                      {product.status === 'active' ? t('orderNow') : t('outOfStock')}
                    </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Product Detail Modal - full card with full description */}
      {showProductDetailModal && selectedProductForDetail && (
        <div 
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => { setShowProductDetailModal(false); setSelectedProductForDetail(null); }}
        >
          <div 
            className="relative bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden border border-gray-700 flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-6 p-6 overflow-y-auto">
              {/* Product Image - also clickable to enlarge */}
              <div 
                className="flex-shrink-0 w-48 h-48 rounded-xl overflow-hidden cursor-zoom-in bg-gray-700"
                onClick={() => { setEnlargedImageUrl(selectedProductForDetail.image); setShowImageEnlargeModal(true); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setEnlargedImageUrl(selectedProductForDetail.image); setShowImageEnlargeModal(true); } }}
              >
                <img
                  src={selectedProductForDetail.image}
                  alt={selectedProductForDetail.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/300x300?text=No+Image'; }}
                />
              </div>
              <div className="flex-1 min-w-0">
                {selectedProductForDetail.vendor && (
                  <Link
                    to={`/${selectedProductForDetail.vendor}/shop`}
                    className="inline-flex items-center gap-1.5 text-cyan-400 hover:text-cyan-300 text-sm font-medium mb-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="fas fa-user"></i>
                    {selectedProductForDetail.vendor}
                  </Link>
                )}
                <h2 className="text-xl font-bold text-white mb-2">{selectedProductForDetail.name}</h2>
                {selectedProductForDetail.description && (
                  <div 
                    className="text-gray-400 text-sm mb-4 [&_p]:mb-2 [&_p:last-child]:mb-0"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedProductForDetail.description) }}
                  />
                )}
                {selectedProductForDetail.stock > 0 && (
                  <p className="text-xs text-gray-500 mb-2">{selectedProductForDetail.stock} in stock</p>
                )}
                {selectedProductForDetail.createdAt && (
                  <p className="text-xs text-gray-400 mb-4">
                    <i className="fa-regular fa-calendar mr-1.5"></i>
                    {formatProductDate(selectedProductForDetail.createdAt)}
                  </p>
                )}
                <button
                  onClick={() => {
                    setShowProductDetailModal(false);
                    setSelectedProductForDetail(null);
                    handleOrderNow(selectedProductForDetail);
                  }}
                  disabled={selectedProductForDetail.status !== 'active'}
                  className={`w-full py-2.5 px-4 rounded-lg font-medium transition-all ${
                    selectedProductForDetail.status === 'active'
                      ? 'bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white'
                      : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {selectedProductForDetail.status === 'active' ? t('orderNow') : t('outOfStock')}
                </button>
              </div>
            </div>
            <button
              onClick={() => { setShowProductDetailModal(false); setSelectedProductForDetail(null); }}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-gray-700 hover:bg-gray-600 text-white flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Image Enlarge Modal */}
      {showImageEnlargeModal && enlargedImageUrl && (
        <div 
          className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/90 p-4 cursor-zoom-out"
          onClick={() => { setShowImageEnlargeModal(false); setEnlargedImageUrl(null); }}
        >
          <img
            src={enlargedImageUrl}
            alt="Enlarged product"
            className="max-w-[95vw] max-h-[95vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => { setShowImageEnlargeModal(false); setEnlargedImageUrl(null); }}
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600 text-white flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Create Order Modal */}
      {showCreateOrderModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col border border-gray-700">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-pink-600 to-purple-600 px-8 py-6 flex-shrink-0 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">{t('shopCreateOrder')}</h2>
                    <p className="text-pink-100 mt-1">{t('shopFillDetailsToPlaceOrder')}</p>
                  </div>
                </div>
                <button onClick={closeCreateOrderModal} className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8 overflow-y-auto sidebar-scroll flex-1">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-600">
                    <h3 className="text-lg font-semibold text-white mb-4">{t('shopProducts')}</h3>
                    {orderForm.lineItems.length > 0 && orderForm.lineItems.some(item => item.title.trim() !== '') && (
                      <div className="mb-6">
                        <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-400 mb-3 px-4">
                          <div className="col-span-6">{t('shopProduct')}</div>
                          <div className="col-span-2 text-center">{t('shopQuantity')}</div>
                          <div className="col-span-3 text-right">{t('shopTotal')}</div>
                          <div className="col-span-1"></div>
                        </div>
                        <div className="space-y-3">
                          {orderForm.lineItems.filter(item => item.title.trim() !== '').map((item, index) => (
                            <div key={index} className="grid grid-cols-12 gap-4 items-center py-3 px-4 bg-gray-800 rounded-lg border border-gray-600">
                              <div className="col-span-6 flex items-center space-x-3">
                                <img src={selectedProduct?.image || 'https://via.placeholder.com/150'} alt={item.title} className="w-10 h-10 rounded-lg object-cover bg-gray-700" onError={(e) => { e.target.src = "https://via.placeholder.com/150"; }} />
                                <div>
                                  <h4 className="text-white font-medium text-sm">{item.title}</h4>
                                  <p className="text-gray-400 text-xs">€{parseFloat(item.price || 0).toFixed(2)}</p>
                                </div>
                              </div>
                              <div className="col-span-2 flex justify-center">
                                <input type="number" min="1" value={item.quantity} onChange={(e) => handleLineItemChange(index, 'quantity', parseInt(e.target.value) || 1)} className="w-16 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-center text-sm" />
                              </div>
                              <div className="col-span-3 text-right">
                                <span className="text-white font-medium">€{(parseFloat(item.price || 0) * item.quantity).toFixed(2)}</span>
                              </div>
                              <div className="col-span-1 flex justify-end">
                                <button onClick={() => removeLineItem(index)} className="text-gray-400 hover:text-red-400 p-1 rounded transition-colors">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Payment Summary */}
                    <div className="mt-8 bg-gray-800 rounded-xl p-6 border border-gray-600">
                      <h4 className="text-lg font-semibold text-white mb-4">{t('shopPayment')}</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between text-gray-300">
                          <span>{t('shopSubtotal')}</span>
                          <span>€{orderForm.lineItems.reduce((sum, item) => sum + (parseFloat(item.price || 0) * item.quantity), 0).toFixed(2)}</span>
                        </div>
                        <div className="border-t border-gray-600 pt-3">
                          <div className="flex justify-between text-white text-xl font-bold">
                            <span>{t('shopTotal')}</span>
                            <span>€{(orderForm.lineItems.reduce((sum, item) => sum + (parseFloat(item.price || 0) * item.quantity), 0) + orderForm.totalTax).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Shipping Info */}
                    <div className="mt-6 bg-gray-700/50 rounded-xl p-6 border border-gray-600">
                      <h3 className="text-lg font-semibold text-white mb-4">{t('shopShippingInformation')}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">{t('shopFirstName')} <span className="text-red-400">*</span></label>
                          <input type="text" value={orderForm.first_name} onChange={(e) => handleFormChange('first_name', e.target.value)} className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent" placeholder={t('shopFirstName')} required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">{t('shopLastName')} <span className="text-red-400">*</span></label>
                          <input type="text" value={orderForm.last_name} onChange={(e) => handleFormChange('last_name', e.target.value)} className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent" placeholder={t('shopLastName')} required />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-300 mb-2">{t('shopCustomerEmail')} <span className="text-red-400">*</span></label>
                          <input type="email" value={orderForm.email} onChange={(e) => handleFormChange('email', e.target.value)} className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent" placeholder="Email" required />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-300 mb-2">{t('shopAddress')} <span className="text-red-400">*</span></label>
                          <input type="text" value={orderForm.address1} onChange={(e) => handleFormChange('address1', e.target.value)} className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent" placeholder={t('shopAddress')} required />
                        </div>
                        {/* 2-column: Left = Phone, ZIP | Right = Country, City */}
                        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Left column */}
                          <div className="space-y-4">
                            <div className="relative">
                              <label className="block text-sm font-medium text-gray-300 mb-2">{t('shopPhone')} <span className="text-red-400">*</span></label>
                              <div className="flex w-full rounded-lg border border-gray-600 bg-gray-800 focus-within:ring-2 focus-within:ring-pink-500 focus-within:border-transparent min-h-[48px]">
                                <div className="relative flex-shrink-0" ref={phoneCountryDropdownRef}>
                                  <button
                                    type="button"
                                    onClick={() => setShowPhoneCountryDropdown(prev => !prev)}
                                    className="flex items-center gap-2 px-4 py-3 bg-gray-800 border-r border-gray-600 text-white hover:bg-gray-700 min-w-[120px] rounded-l-lg h-full"
                                  >
                                    {(() => {
                                      const selected = COUNTRY_PHONE_CODES.find(c => c.code === orderForm.phoneCountryCode);
                                      const code = selected?.countryCode || 'ES';
                                      return (
                                        <span
                                          className={`fi fi-${code.toLowerCase()}`}
                                          style={{
                                            width: '22px',
                                            height: '16px',
                                            borderRadius: '3px',
                                            display: 'inline-block',
                                            flexShrink: 0
                                          }}
                                          role="img"
                                          aria-label={selected?.country || 'Afghanistan'}
                                        />
                                      );
                                    })()}
                                    <span className="text-cyan-400 font-semibold text-sm">
                                      {COUNTRY_PHONE_CODES.find(c => c.code === orderForm.phoneCountryCode)?.code || '+93'}
                                    </span>
                                    <svg className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-auto ${showPhoneCountryDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                  {showPhoneCountryDropdown && ReactDOM.createPortal(
                                    <div
                                      data-phone-country-dropdown
                                      className="fixed w-72 bg-gray-800 rounded-lg shadow-xl z-[10001] max-h-64 overflow-hidden border border-gray-600"
                                      style={{ top: phoneDropdownPosition.top, left: phoneDropdownPosition.left }}
                                    >
                                      <div className="p-2">
                                        <div className="text-xs text-gray-400 px-2 py-1.5 font-medium">{t('selectLocation')}</div>
                                        <input
                                          type="text"
                                          value={phoneCountrySearchQuery}
                                          onChange={(e) => setPhoneCountrySearchQuery(e.target.value)}
                                          placeholder={t('searchCountry')}
                                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent mb-2"
                                        />
                                        <div className="max-h-44 overflow-y-auto">
                                          {(() => {
                                            const filtered = phoneCountrySearchQuery
                                              ? COUNTRY_PHONE_CODES.filter(c =>
                                                  c.country.toLowerCase().includes(phoneCountrySearchQuery.toLowerCase()) ||
                                                  c.code.includes(phoneCountrySearchQuery)
                                                )
                                              : COUNTRY_PHONE_CODES;
                                            if (filtered.length === 0) {
                                              return <div className="text-center py-4 text-gray-400 text-sm">{t('noCountryFound')}</div>;
                                            }
                                            return filtered.map((c) => (
                                              <button
                                                key={c.code + c.countryCode}
                                                type="button"
                                                onClick={() => {
                                                  handleFormChange('phoneCountryCode', c.code);
                                                  setShowPhoneCountryDropdown(false);
                                                  setPhoneCountrySearchQuery('');
                                                }}
                                                className={`w-full flex items-center px-3 py-2 text-left hover:bg-gray-700 rounded transition-colors text-sm ${
                                                  orderForm.phoneCountryCode === c.code ? 'bg-gray-700' : ''
                                                }`}
                                              >
                                                <span
                                                  className={`fi fi-${c.countryCode.toLowerCase()} mr-3`}
                                                  style={{
                                                    width: '24px',
                                                    height: '16px',
                                                    borderRadius: '3px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                  }}
                                                  role="img"
                                                  aria-label={`${c.country} flag`}
                                                />
                                                <span className="text-white text-sm flex-1">{c.country}</span>
                                                <span className="text-cyan-400 text-xs">{c.code}</span>
                                              </button>
                                            ));
                                          })()}
                                        </div>
                                      </div>
                                    </div>,
                                    document.body
                                  )}
                                </div>
                                <input
                                  type="tel"
                                  value={orderForm.phone}
                                  onChange={(e) => handleFormChange('phone', e.target.value)}
                                  className="flex-1 min-w-0 bg-transparent px-4 py-3 text-white focus:outline-none placeholder-gray-500 rounded-r-lg border-0"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">{t('shopZipCode')}</label>
                              <input type="text" value={orderForm.zip} onChange={(e) => handleFormChange('zip', e.target.value)} className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent" placeholder={t('shopZipCode')} />
                            </div>
                          </div>
                          {/* Right column */}
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">{t('shopCountry')}</label>
                              <input type="text" value={orderForm.country} onChange={(e) => handleFormChange('country', e.target.value)} className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent" placeholder={t('shopCountry')} />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">{t('shopCity')}</label>
                              <input type="text" value={orderForm.city} onChange={(e) => handleFormChange('city', e.target.value)} className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent" placeholder={t('shopCity')} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Mockup Generation */}
                  <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-600">
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <i className="fa-solid fa-shirt mr-2 text-purple-400"></i>
                      {t('shopGenerateMockup')}
                    </h3>
                    <p className="text-gray-400 text-xs font-medium mb-3">{t('shopSelectProductType')}</p>
                    <div className="divide-y divide-gray-600 border border-gray-600 rounded-lg overflow-hidden">
                      {getAvailableTypes(selectedProduct?.products).map((product) => (
                        <button
                          key={product.id}
                          onClick={() => openMockupEditor(product.id)}
                          className="w-full flex items-center gap-2 px-4 py-3 bg-gray-800 hover:bg-purple-600/30 transition-colors duration-150 text-left"
                        >
                          <span className="text-lg">{product.icon}</span>
                          <span className="text-white font-medium text-sm capitalize">{product.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Saved Mockup Preview (only shown after generation) */}
                  {savedMockup && (
                    <div className="bg-gray-700/50 rounded-xl p-6 border border-purple-500/50">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                        <i className="fa-solid fa-image mr-2 text-purple-400"></i>
                        {t('shopMockupPreview')}
                      </h3>
                      <div className="space-y-4">
                        <div className="rounded-lg overflow-hidden bg-gray-800 border border-gray-600">
                          <img 
                            src={savedMockup.imageUrl} 
                            alt="Mockup Preview" 
                            className="w-full h-auto"
                          />
                        </div>
                        {/* Order details: Type, Size, Color, Quality (t-shirt only) */}
                        <div className="flex flex-wrap gap-2">
                          <span className="inline-flex items-center gap-1.5 bg-gray-800 text-gray-300 text-xs px-2.5 py-1.5 rounded border border-gray-600">
                            <i className="fa-solid fa-shirt text-purple-400"></i>
                            {savedMockup.type}
                          </span>
                          {savedMockup.size && (
                            <span className="inline-flex items-center gap-1.5 bg-gray-800 text-gray-300 text-xs px-2.5 py-1.5 rounded border border-gray-600">
                              <i className="fa-solid fa-ruler text-blue-400"></i>
                              {savedMockup.size}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1.5 bg-gray-800 text-gray-300 text-xs px-2.5 py-1.5 rounded border border-gray-600">
                            <i className="fa-solid fa-palette text-pink-400"></i>
                            <span className="w-3 h-3 rounded border border-gray-500" style={{ backgroundColor: savedMockup.color }}></span>
                            {savedMockup.color}
                          </span>
                          {savedMockup.quality && (
                            <span className="inline-flex items-center gap-1.5 bg-gray-800 text-gray-300 text-xs px-2.5 py-1.5 rounded border border-gray-600">
                              <i className="fa-solid fa-gem text-amber-400"></i>
                              {savedMockup.quality}
                            </span>
                          )}
                        </div>
                        <div className="flex space-x-3">
                          <button
                            onClick={() => setSavedMockup(null)}
                            className="flex-1 px-4 py-3 bg-gray-600 hover:bg-red-600 text-white font-medium rounded-lg transition-all flex items-center justify-center space-x-2"
                          >
                            <i className="fa-solid fa-trash"></i>
                            <span>{t('shopDelete')}</span>
                          </button>
                          <button
                            onClick={handleEditMockup}
                            className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg transition-all flex items-center justify-center space-x-2"
                          >
                            <i className="fa-solid fa-pencil"></i>
                            <span>{t('shopEditMockup')}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-700 px-8 py-6 border-t border-gray-600 flex-shrink-0 rounded-b-2xl">
              <div className="flex justify-end space-x-4">
                <button onClick={closeCreateOrderModal} className="px-6 py-3 border border-gray-600 rounded-lg text-gray-300 bg-gray-800 hover:bg-gray-700 transition-colors font-medium">
                  {t('shopCancel')}
                </button>
                <button onClick={handleCreateOrderClick} disabled={creatingOrder} className="px-6 py-3 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg hover:from-pink-700 hover:to-purple-700 transition-all font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  {creatingOrder ? (
                    <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /><span>{t('shopProcessing')}</span></>
                  ) : (
                    <><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg><span>{t('shopCreateOrder')}</span></>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Waiting for payment loader - shown while popup is open */}
      {waitingForPayment && !sendingInvoice && (
        <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl shadow-xl px-8 py-6 border border-gray-700 flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-white font-medium">Waiting for payment...</p>
            <p className="text-gray-400 text-sm">Complete payment in the popup window.</p>
          </div>
        </div>
      )}

      {/* Generating invoice loader - shown while sending invoice email */}
      {sendingInvoice && (
        <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl shadow-xl px-8 py-6 border border-gray-700 flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-white font-medium">{t('shopGeneratingInvoice')}</p>
            <p className="text-gray-400 text-sm">{t('shopSendingToEmail')}</p>
          </div>
        </div>
      )}

      {/* Invoice/Receipt Modal - shown after order success */}
      {showInvoiceModal && lastCreatedOrder && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl shadow-xl max-w-lg w-full mx-4 border border-gray-700 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">{t('shopOrderReceipt')}</h2>
                    <p className="text-emerald-100 text-sm mt-0.5">Order #{lastCreatedOrder.orderNumber || lastCreatedOrder.id}</p>
                  </div>
                </div>
                <button onClick={() => { setShowInvoiceModal(false); setLastCreatedOrder(null); setLastCreatedOrderForm(null); setLastSavedMockup(null); setInvoiceEmailSent(false); }} className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-4">
                <div className="bg-gray-700/50 rounded-xl p-4 border border-gray-600">
                  <p className="text-gray-400 text-sm">{t('shopCustomer')}</p>
                  <p className="text-white font-medium">{lastCreatedOrderForm?.first_name} {lastCreatedOrderForm?.last_name}</p>
                  <p className="text-cyan-400 text-sm">{lastCreatedOrderForm?.email || lastCreatedOrder.email}</p>
                </div>
                <div className="bg-gray-700/50 rounded-xl p-4 border border-gray-600">
                  <p className="text-gray-400 text-sm mb-2">{t('shopShippingAddress')}</p>
                  <p className="text-white text-sm">
                    {lastCreatedOrderForm?.address1}<br />
                    {[lastCreatedOrderForm?.city, lastCreatedOrderForm?.province, lastCreatedOrderForm?.zip].filter(Boolean).join(', ')}<br />
                    {lastCreatedOrderForm?.country}
                  </p>
                </div>
                <div className="bg-gray-700/50 rounded-xl p-4 border border-gray-600">
                  <p className="text-gray-400 text-sm mb-3">{t('shopOrderDetails')}</p>
                  <div className="space-y-2">
                    {(lastCreatedOrderForm?.lineItems || lastCreatedOrder?.lineItems || lastCreatedOrder?.line_items || []).filter(i => i.title?.trim()).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-gray-300">{item.title} x{item.quantity || 1}</span>
                        <span className="text-white">€{(parseFloat(item.price || 0) * (item.quantity || 1)).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-600 mt-3 pt-3 flex justify-between text-white font-bold">
                    <span>{t('shopTotal')}</span>
                    <span>€{(Number((lastCreatedOrderForm?.lineItems || lastCreatedOrder?.lineItems || lastCreatedOrder?.line_items || []).reduce((s, i) => s + (parseFloat(i.price || 0) * (i.quantity || 1)), 0)) + (parseFloat(lastCreatedOrderForm?.totalTax ?? lastCreatedOrder?.totalTax ?? lastCreatedOrder?.total_tax ?? 0) || 0)).toFixed(2)}</span>
                  </div>
                </div>
                {lastSavedMockup?.type && (
                  <div className="bg-gray-700/50 rounded-xl p-4 border border-gray-600">
                    <p className="text-gray-400 text-sm mb-1">{t('shopCustomization')}</p>
                    <p className="text-gray-300 text-sm">{lastSavedMockup.type} {lastSavedMockup.size || ''} {lastSavedMockup.quality || ''}</p>
                  </div>
                )}
                <div className={`rounded-xl p-4 border ${invoiceEmailSent ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
                  {sendingInvoice ? (
                    <p className="text-amber-400 text-sm flex items-center gap-2"><span className="animate-spin">⏳</span> {t('shopSendingInvoiceToEmail')}</p>
                  ) : invoiceEmailSent ? (
                    <p className="text-emerald-400 text-sm flex items-center gap-2">✓ {t('shopInvoiceSentTo')} {lastCreatedOrderForm?.email || lastCreatedOrder.email}</p>
                  ) : (
                    <p className="text-amber-400 text-sm">{t('shopPreparingToSendInvoice')}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-700/30 border-t border-gray-600 flex justify-end rounded-b-2xl">
              <button onClick={() => { setShowInvoiceModal(false); setLastCreatedOrder(null); setLastCreatedOrderForm(null); setLastSavedMockup(null); setInvoiceEmailSent(false); }} className="px-6 py-2.5 bg-gray-600 hover:bg-gray-500 text-white rounded-lg font-medium transition-colors">
                {t('shopDone')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mockup Preview Modal */}
      {showMockupModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-[10001] flex items-center justify-center p-4"
          onClick={closeMockupModal}
        >
          <div 
            className="relative bg-gray-900 rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col shadow-2xl border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4 flex items-center justify-between flex-shrink-0 rounded-t-2xl">
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-shirt text-white text-xl"></i>
                <h3 className="text-white font-bold text-lg">Mockup Preview</h3>
              </div>
              <button
                onClick={closeMockupModal}
                className="text-white hover:text-gray-200 transition-colors p-2"
              >
                <i className="fa-solid fa-times text-xl"></i>
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-5">
                {/* Product Type Badge */}
                <div className="flex items-center justify-center">
                  <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full px-4 py-2 border border-purple-500/30">
                    <span className="text-white font-medium">
                      {productTypes.find(p => p.id === currentMockupProductType)?.icon}{' '}
                      {productTypes.find(p => p.id === currentMockupProductType)?.label}
                    </span>
                  </div>
                </div>

                {/* 2-Column Layout: Preview + Controls */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Column 1: Preview */}
                  <div className="space-y-3">
                    <div ref={mockupPreviewRef} className="bg-gray-800 rounded-xl p-4 shadow-lg">
                      <div className="relative w-full overflow-hidden rounded-lg bg-gray-700">
                        <div className="relative">
                          {/* SVG filter for product colorization */}
                          <svg style={{ position: 'absolute', width: 0, height: 0 }}>
                            <defs>
                              <filter id="productColorFilter">
                                <feColorMatrix
                                  type="matrix"
                                  values={(() => {
                                    const hex = (mockupColor.startsWith('#') ? mockupColor : '#FFFFFF').replace('#', '').padEnd(6, '0').substring(0, 6);
                                    const r = (parseInt(hex.substring(0, 2), 16) || 255) / 255;
                                    const g = (parseInt(hex.substring(2, 4), 16) || 255) / 255;
                                    const b = (parseInt(hex.substring(4, 6), 16) || 255) / 255;
                                    return `${r} 0 0 0 0  0 ${g} 0 0 0  0 0 ${b} 0 0  0 0 0 1 0`;
                                  })()}
                                />
                              </filter>
                            </defs>
                          </svg>
                          {/* Real blank product template with color tint */}
                          {productTemplates[currentMockupProductType]?.image ? (
                            <img 
                              src={productTemplates[currentMockupProductType].image} 
                              alt={productTemplates[currentMockupProductType].title || currentMockupProductType}
                              className="w-full h-auto rounded-lg"
                              style={{ filter: (mockupColor.startsWith('#') && mockupColor !== '#FFFFFF') ? 'url(#productColorFilter)' : 'none' }}
                            />
                          ) : (
                            <div style={{ paddingBottom: '100%' }} className="bg-gray-600 rounded-lg"></div>
                          )}
                          {/* Design image overlay positioned by sliders */}
                          {selectedProduct?.image && (
                            <div className="absolute inset-0 pointer-events-none">
                              <img 
                                src={selectedProduct.image} 
                                alt="Design"
                                style={{
                                  position: 'absolute',
                                  left: `${positionX}%`,
                                  top: `${positionY}%`,
                                  transform: `translate(-50%, -50%) scale(${mockupScale / 100})`,
                                  maxWidth: '50%',
                                  maxHeight: '50%',
                                  objectFit: 'contain',
                                  transition: 'all 0.15s ease-out',
                                  opacity: 0.85,
                                  filter: 'drop-shadow(0 0 6px rgba(168,85,247,0.5))',
                                }}
                              />
                              {/* Crosshair guides */}
                              <div style={{ position: 'absolute', left: `${positionX}%`, top: 0, bottom: 0, width: '1px', background: 'rgba(168,85,247,0.3)' }}></div>
                              <div style={{ position: 'absolute', top: `${positionY}%`, left: 0, right: 0, height: '1px', background: 'rgba(168,85,247,0.3)' }}></div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Column 2: Controls */}
                  <div className="space-y-4">
                    {/* Position & Size Controls */}
                    <div className="bg-gray-800 rounded-xl p-5 border-2 border-purple-500/30 space-y-4">
                      <h4 className="text-white font-semibold text-sm flex items-center">
                        <i className="fa-solid fa-crosshairs mr-2 text-purple-400"></i>
                        Position & Size
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-gray-400 text-xs font-medium">↔️ X Position</span>
                            <span className="text-purple-400 text-xs font-bold bg-gray-700 px-2 py-0.5 rounded">{positionX}%</span>
                          </div>
                          <input type="range" min="0" max="100" value={positionX} onChange={(e) => setPositionX(parseInt(e.target.value))} className="w-full h-1.5 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-purple-500" />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-gray-400 text-xs font-medium">↕️ Y Position</span>
                            <span className="text-purple-400 text-xs font-bold bg-gray-700 px-2 py-0.5 rounded">{positionY}%</span>
                          </div>
                          <input type="range" min="0" max="100" value={positionY} onChange={(e) => setPositionY(parseInt(e.target.value))} className="w-full h-1.5 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-purple-500" />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-gray-400 text-xs font-medium">📐 Scale</span>
                            <span className="text-purple-400 text-xs font-bold bg-gray-700 px-2 py-0.5 rounded">{mockupScale}%</span>
                          </div>
                          <input type="range" min="10" max="200" value={mockupScale} onChange={(e) => setMockupScale(parseInt(e.target.value))} className="w-full h-1.5 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-purple-500" />
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button onClick={resetPositionToAuto} className="flex-1 px-3 py-2 bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-700 hover:to-red-700 text-white text-xs font-medium rounded-lg transition-all flex items-center justify-center space-x-1">
                          <i className="fa-solid fa-rotate-left"></i>
                          <span>Reset</span>
                        </button>
                        <button onClick={centerDesign} className="flex-1 px-3 py-2 bg-gradient-to-r from-green-500 to-cyan-500 hover:from-green-600 hover:to-cyan-600 text-white text-xs font-medium rounded-lg transition-all flex items-center justify-center space-x-1">
                          <i className="fa-solid fa-bolt"></i>
                          <span>Center</span>
                        </button>
                      </div>
                    </div>

                    {/* Size Selection */}
                    <div className="bg-gray-800 rounded-xl p-5 border border-gray-600">
                      <h4 className="text-white font-semibold text-sm flex items-center mb-3">
                        <i className="fa-solid fa-ruler mr-2 text-blue-400"></i>
                        Size <span className="text-red-400 ml-1">*</span>
                      </h4>
                      <select
                        value={mockupSize}
                        onChange={(e) => setMockupSize(e.target.value)}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      >
                        <option value="">Select size...</option>
                        {(sizeOptionsByType[currentMockupProductType] || []).map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Color Selection */}
                    <div className="bg-gray-800 rounded-xl p-5 border border-gray-600">
                      <h4 className="text-white font-semibold text-sm flex items-center mb-3">
                        <i className="fa-solid fa-palette mr-2 text-pink-400"></i>
                        Color
                      </h4>
                      <div className="flex items-center space-x-3">
                        <input
                          type="color"
                          value={mockupColor.startsWith('#') ? mockupColor : '#FFFFFF'}
                          onChange={(e) => setMockupColor(e.target.value)}
                          className="w-12 h-10 rounded-lg border border-gray-600 cursor-pointer bg-transparent"
                        />
                        <input
                          type="text"
                          value={mockupColor}
                          onChange={(e) => setMockupColor(e.target.value)}
                          className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent uppercase text-sm"
                          placeholder="#FFFFFF"
                        />
                      </div>
                    </div>

                    {/* Quality Selection (T-Shirt only) */}
                    {currentMockupProductType === 'tshirt' && (() => {
                      const qualities = ['Standard', 'Premium', 'Deluxe'];
                      const qualityOptions = qualities.map(q => {
                        const v = findVariantByType(selectedProduct?.variants, currentMockupProductType, q);
                        const price = parseFloat(v?.price || 0).toFixed(2);
                        return { value: q, label: `${q} (€${price})` };
                      });
                      return (
                        <div className="bg-gray-800 rounded-xl p-5 border border-gray-600">
                          <h4 className="text-white font-semibold text-sm flex items-center mb-3">
                            <i className="fa-solid fa-gem mr-2 text-amber-400"></i>
                            Quality
                          </h4>
                          <select
                            value={mockupQuality}
                            onChange={(e) => {
                              const q = e.target.value;
                              setMockupQuality(q);
                              updateLineItemPrice(currentMockupProductType, q);
                            }}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                          >
                            {qualityOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      );
                    })()}

                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-800 px-6 py-4 border-t border-gray-700 flex-shrink-0 rounded-b-2xl">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={closeMockupModal}
                  className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProceedMockup}
                  disabled={savingMockup || !mockupSize}
                  className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {savingMockup ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-check"></i>
                      <span>Proceed</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Shop;
